import { FC } from 'react';
import { ResponsiveRadialBar } from '@nivo/radial-bar';

import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';

import { formatCompactChartValue, humanizeChartLabel, truncateChartLabel } from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

const data = [
    {
        id: 'Supermarket',
        data: [
            {
                x: 'Vegetables',
                y: 251,
            },
            {
                x: 'Fruits',
                y: 253,
            },
            {
                x: 'Meat',
                y: 271,
            },
            {
                x: 'Fish',
                y: 108,
            },
        ],
    },
    {
        id: 'Combini',
        data: [
            {
                x: 'Vegetables',
                y: 92,
            },
            {
                x: 'Fruits',
                y: 80,
            },
            {
                x: 'Meat',
                y: 124,
            },
            {
                x: 'Fish',
                y: 294,
            },
        ],
    },
    {
        id: 'Online',
        data: [
            {
                x: 'Vegetables',
                y: 138,
            },
            {
                x: 'Fruits',
                y: 39,
            },
            {
                x: 'Meat',
                y: 226,
            },
            {
                x: 'Fish',
                y: 278,
            },
        ],
    },
    {
        id: 'Marché',
        data: [
            {
                x: 'Vegetables',
                y: 60,
            },
            {
                x: 'Fruits',
                y: 26,
            },
            {
                x: 'Meat',
                y: 272,
            },
            {
                x: 'Fish',
                y: 152,
            },
        ],
    },
];

interface RadialChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const RadialChart: FC<RadialChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const chartData = useNivoReportData(component, data);
    const { financialValueDisplay, formatMetricValue } = useNivoReportFormatting(component);

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveRadialBar
                animate={true}
                data={chartData}
                valueFormat={(value) => formatMetricValue(value)}
                padding={0.25}
                cornerRadius={4}
                colors={colorsArray}
                borderWidth={1}
                innerRadius={0.5}
                padAngle={1}
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 0.2]],
                }}
                motionConfig={'gentle'}
                isInteractive={true}
                enableTracks={false}
                theme={nivoChartTheme}
                margin={{ top: 36, right: 36, bottom: 36, left: 36 }}
                radialAxisStart={{
                    format: (value) =>
                        formatCompactChartValue(
                            value instanceof Date ? value.toISOString() : value,
                            formatMetricValue,
                            financialValueDisplay,
                        ),
                    tickPadding: 5,
                    tickRotation: 0,
                    tickSize: 5,
                }}
                circularAxisOuter={{
                    format: (value) => truncateChartLabel(value, 14),
                    tickPadding: 12,
                    tickRotation: 0,
                    tickSize: 5,
                }}
                label={(bar) => truncateChartLabel(bar.category, 12)}
                tooltip={({ bar }) => (
                    <CustomNivoChartTooltip
                        title={humanizeChartLabel(bar.groupId)}
                        entries={[
                            {
                                color: bar.color,
                                label: bar.category,
                                value: bar.value,
                            },
                        ]}
                        formatValue={formatMetricValue}
                    />
                )}
                legends={[]}
            />
        </div>
    );
};

export default RadialChart;
